import * as React from "react";
import { useEffect, useState } from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Paper,
  Typography,
  Stack,
  Chip,
  Skeleton,
  alpha,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  Tabs,
  Tab,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import Chart from "react-apexcharts";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import RemoveShoppingCartOutlinedIcon from "@mui/icons-material/RemoveShoppingCartOutlined";
import ArrowDownwardOutlinedIcon from "@mui/icons-material/ArrowDownwardOutlined";
import ArrowUpwardOutlinedIcon from "@mui/icons-material/ArrowUpwardOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import StarBorderRoundedIcon from "@mui/icons-material/StarBorderRounded";
import StarRoundedIcon from "@mui/icons-material/StarRounded";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import RefreshIcon from "@mui/icons-material/Refresh";
import { unstable_batchedUpdates } from "react-dom";
import { useNavigate } from "react-router-dom";
import LocationSelect from "./pickers/LocationSelect";
import Filters from "../Filters/Filters";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import {
  fetchInventorySummary,
  fetchStockByLocation,
  fetchTopMovingProducts,
  fetchDeadStock,
  fetchTopStockItems,
  fetchLowStockItems,
  fetchItemMovementHistory,
} from "../../store/inventoryDashboardStore/inventoryDashboardThunk";
import { Utils } from "../../utils/common-utils";
import { movementReasonLabel } from "../../utils/inventoryConstants";

// Inventory Dashboard — quantities and counts only (valuation lives in the
// accounting reports). Summary sections render inline; the heavy, unbounded
// data (stock in hand, movements, GRNs, requisitions, transfers, batches) is a
// grid of report cards that REDIRECT to their full pages — a dashboard table
// can't show 10k rows.
// Same card tokens as the Home dashboard so both read as one family — flat
// white cards, 1px #cbd5e1 border, no shadows, app-wide green #059669.
const ACCENT = "#059669";
const ACCENT_SOFT = "#ecfdf5";
const BG = "#fff";
const SURFACE = "#ffffff";
const TEXT = "#292d35";
const MUTED = "#9da7b7";
const BORDER = "#cbd5e1";

const cardSx = {
  borderRadius: 4,
  border: `1px solid ${BORDER}`,
  boxShadow: "none",
  backgroundColor: SURFACE,
};

// Tab styling shared with the item view / stock pages.
const tabsSx = {
  minHeight: 32,
  mb: 0.5,
  "& .MuiTab-root": { minHeight: 32, py: 0, fontSize: "0.78rem", textTransform: "none", fontWeight: 600, color: "#545454" },
  "& .Mui-selected": { color: "#059669 !important" },
  "& .MuiTabs-indicator": { backgroundColor: "#059669" },
};

const fmtQty = (n: any): string => (Number(n) || 0).toLocaleString("en-IN");

const daysAgo = (dateStr: string | null): string => {
  if (!dateStr) return "Never moved";
  const d = Math.floor((Date.now() - new Date(dateStr).getTime()) / (24 * 60 * 60 * 1000));
  if (d < 1) return "Today";
  if (d === 1) return "1 day ago";
  return `${d} days ago`;
};

const reasonAccent = (reason: string): string => {
  if (["grn", "opening", "production_yield", "return", "transfer_in"].includes(reason)) return "#22c55e";
  if (["sale", "wastage", "transfer_out", "production_consume"].includes(reason)) return "#ef4444";
  return "#3b82f6";
};

const InventoryDashboard: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const {
    summary,
    byLocation,
    topProducts,
    deadStock,
    topStock,
    lowStock,
    status,
  } = useAppSelector((state) => state.inventoryDashboard);

  const [isLoading, setIsLoading] = useState(false);
  // Location / date-range filters — pushed to every fetch server-side.
  const [locationId, setLocationId] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [filtersKey, setFiltersKey] = useState(0); // bumped on reset to remount the date dropdown
  const [stockTab, setStockTab] = useState(0); // 0 = Top Stock, 1 = Low Stock

  // Item movement-history drill-down (opened by clicking an item anywhere).
  const [historySkuId, setHistorySkuId] = useState<string | null>(null);
  const [historyData, setHistoryData] = useState<{ sku: any; movements: any[] } | null>(null);
  const [historyLoading, setHistoryLoading] = useState(false);

  const openItemHistory = async (skuId: string) => {
    setHistorySkuId(skuId);
    setHistoryLoading(true);
    try {
      const data = await fetchItemMovementHistory(skuId, 50);
      setHistoryData(data);
    } catch {
      setHistoryData(null);
    } finally {
      setHistoryLoading(false);
    }
  };
  const closeItemHistory = () => {
    setHistorySkuId(null);
    setHistoryData(null);
  };

  // Appends the active Location / Date filters to a base query string.
  const withFilters = (base: string) =>
    [base, locationId ? `locationId=${locationId}` : "", fromDate ? `from=${fromDate}` : "", toDate ? `to=${toDate}` : ""]
      .filter(Boolean)
      .join("&");

  const loadAll = () => {
    setIsLoading(true);
    Promise.allSettled([
      dispatch(fetchInventorySummary(withFilters(""))),
      dispatch(fetchStockByLocation()),
      dispatch(fetchTopMovingProducts(withFilters("days=30&limit=8"))),
      dispatch(fetchDeadStock(withFilters("days=60&limit=25"))),
      dispatch(fetchTopStockItems(withFilters("limit=8"))),
      dispatch(fetchLowStockItems(withFilters("limit=8"))),
    ]).finally(() => setIsLoading(false));
  };

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, locationId, fromDate, toDate]);

  // Shared Filters dropdown emits "start=YYYY-MM-DD&end=YYYY-MM-DD" (empty for "Select Date").
  const handleDateFilter = (qs: string) => {
    const params = new URLSearchParams(qs);
    unstable_batchedUpdates(() => {
      setFromDate(params.get("start") || "");
      setToDate(params.get("end") || "");
    });
  };

  const resetFilters = () => {
    setLocationId(""); setFromDate(""); setToDate("");
    setFiltersKey((k) => k + 1);
  };

  const loading = isLoading && status !== "SUCCESS";

  const MetricCard = ({
    title,
    value,
    icon,
    accent = ACCENT,
    highlight = false,
    sub,
  }: {
    title: string;
    value: string | number;
    icon: React.ReactNode;
    accent?: string;
    highlight?: boolean;
    sub?: string;
  }) => (
    <Card
      sx={{
        ...cardSx,
        backgroundColor: highlight ? ACCENT_SOFT : SURFACE,
        height: "100%",
        minHeight: 100,
      }}
    >
      <CardContent sx={{ p: 1.75, "&:last-child": { pb: 1.75 }, overflow: "hidden", height: "100%", display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
          <Box flex={1} minWidth={0}>
            <Typography sx={{ color: MUTED, fontWeight: 600, fontSize: "0.68rem", textTransform: "uppercase", letterSpacing: "0.4px", lineHeight: 1.3, mb: 0.75 }} noWrap>
              {title}
            </Typography>
            <Typography sx={{ fontWeight: 700, color: TEXT, lineHeight: 1.2, fontSize: "1.05rem" }} noWrap>
              {loading ? <Skeleton width={70} /> : value}
            </Typography>
          </Box>
          <Box sx={{ width: 32, height: 32, borderRadius: 1.5, display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: highlight ? "#ffffff" : alpha(accent, 0.12), color: accent, flexShrink: 0 }}>
            {React.cloneElement(icon as React.ReactElement, { sx: { fontSize: 17, color: accent } })}
          </Box>
        </Stack>
        {sub && (
          <Typography sx={{ color: MUTED, fontSize: "0.65rem", mt: 0.5 }} noWrap>
            {sub}
          </Typography>
        )}
      </CardContent>
    </Card>
  );

  // Report-card grid entry — Reports Dashboard card style: title + favourite star,
  // description, "Available" chip and (where the page supports it) a download
  // button that opens the page with ?download=excel to auto-trigger the export.
  const [favReports, setFavReports] = useState<string[]>(() => {
    try { return JSON.parse(localStorage.getItem("invDashFavReports") || "[]"); } catch { return []; }
  });
  const toggleFavReport = (title: string) => {
    setFavReports((prev) => {
      const next = prev.includes(title) ? prev.filter((t) => t !== title) : [...prev, title];
      localStorage.setItem("invDashFavReports", JSON.stringify(next));
      return next;
    });
  };

  const ReportCard = ({ title, desc, to, download, downloadTo }: { title: string; desc: string; to: string; download?: boolean; downloadTo?: string }) => (
    <Paper
      onClick={() => navigate(to)}
      sx={{
        ...cardSx,
        p: 2,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        cursor: "pointer",
        transition: "border-color .2s ease, background-color .2s ease",
        "&:hover": { borderColor: "#059669", backgroundColor: "#f8fffe" },
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 0.5 }}>
        <Typography sx={{ fontWeight: 700, color: TEXT, fontSize: "0.9rem" }}>{title}</Typography>
        <IconButton
          size="small"
          onClick={(e) => { e.stopPropagation(); toggleFavReport(title); }}
          sx={{ p: 0.25, mt: -0.25, color: favReports.includes(title) ? "#f5b301" : "#9da7b7" }}
        >
          {favReports.includes(title) ? <StarRoundedIcon sx={{ fontSize: 20 }} /> : <StarBorderRoundedIcon sx={{ fontSize: 20 }} />}
        </IconButton>
      </Stack>
      <Typography sx={{ color: MUTED, fontSize: "0.75rem", lineHeight: 1.4, mb: 1.5 }}>{desc}</Typography>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: "auto" }}>
        <Chip
          label="Available"
          size="small"
          sx={{ backgroundColor: "#e3f2fd", color: "#1976d2", fontSize: "0.7rem", fontWeight: 600, height: 22 }}
        />
        {download ?
          <IconButton
            size="small"
            title="Download Excel"
            onClick={(e) => { e.stopPropagation(); navigate(`${downloadTo || to}?download=excel`); }}
            sx={{ p: 0.25, color: "#1976d2" }}
          >
            <FileDownloadOutlinedIcon sx={{ fontSize: 20 }} />
          </IconButton>
          :
          <ArrowForwardIcon sx={{ fontSize: 16, color: "#059669" }} />
        }
      </Stack>
    </Paper>
  );

  const maxLocationQty = Math.max(1, ...byLocation.map((r: any) => r.qty || 0));
  const totalLocationQty = byLocation.reduce((s: number, r: any) => s + (r.qty || 0), 0);

  // ---- Top moving products (horizontal bar) — ranking reads faster as bars.
  // Clicking a bar drills into that item's full movement history. ----
  const topProductsOptions = {
    chart: {
      type: "bar" as const,
      toolbar: { show: false },
      fontFamily: "inherit",
      events: {
        dataPointSelection: (_e: any, _ctx: any, config: any) => {
          const p: any = topProducts[config.dataPointIndex];
          if (p?.skuId) openItemHistory(p.skuId);
        },
      },
    },
    plotOptions: {
      bar: { horizontal: true, barHeight: "55%", borderRadius: 6, borderRadiusApplication: "end" as const, distributed: true, dataLabels: { position: "top" as const } },
    },
    dataLabels: {
      enabled: true,
      textAnchor: "start" as const,
      offsetX: 6,
      formatter: (v: number) => `${v}`,
      style: { fontSize: "11px", fontWeight: 700, colors: [TEXT] },
      background: { enabled: false },
    },
    xaxis: {
      categories: topProducts.map((p: any) => p.name || "—"),
      labels: { style: { colors: MUTED, fontSize: "10px" } },
      axisBorder: { show: false },
      axisTicks: { show: false },
    },
    yaxis: { labels: { style: { colors: TEXT, fontSize: "11px", fontWeight: 600 } } },
    grid: { borderColor: BORDER, strokeDashArray: 4, xaxis: { lines: { show: true } }, yaxis: { lines: { show: false } } },
    legend: { show: false },
    colors: [ACCENT],
    states: { hover: { filter: { type: "darken", value: 0.9 } } },
    tooltip: {
      custom: ({ dataPointIndex }: any) => {
        const p: any = topProducts[dataPointIndex] || {};
        return `<div style="padding:8px 12px;font-family:inherit;background:#fff;"><b style="color:${TEXT};font-size:12px;">${p.name || ""}</b><br/><span style="color:${ACCENT};font-weight:700;font-size:13px;">${p.movedQty} ${p.baseUnitName || "units"} moved</span><div style="font-size:10px;color:${MUTED};margin-top:2px;">${p.movementCount} movements · last 30 days</div><div style="font-size:9px;color:${MUTED};margin-top:4px;font-style:italic;">Click bar for full history</div></div>`;
      },
    },
  };
  const topProductsSeries = [{ name: "Moved qty", data: topProducts.map((p: any) => p.movedQty || 0) }];

  return (
    <Box sx={{ p: { xs: 1.25, sm: 1.5, md: 2 }, backgroundColor: BG, minHeight: "100vh", overflowX: "hidden" }}>
      {/* Header — filters left of the report buttons */}
      <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", md: "center" }} spacing={1.5} sx={{ mb: 1.5 }}>
        <Box>
          <Typography sx={{ fontWeight: 700, color: TEXT, fontSize: { xs: "1.1rem", md: "1.25rem" } }}>Inventory Dashboard</Typography>
          <Typography sx={{ color: MUTED, fontSize: "0.75rem" }}>
            {new Date().toLocaleDateString("en-US", { day: "numeric", month: "short", year: "numeric" })} · stock overview across all locations
          </Typography>
        </Box>
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flexWrap: "wrap" }}>
          <LocationSelect borderless width={180} value={locationId} onChange={(v: string) => setLocationId(v)} label="Location" activeOnly={false} />
          <div style={{ position: "relative", display: "inline-flex", alignItems: "center" }}>
            <span style={{ position: "absolute", top: -8, left: 20, padding: "0 5px", background: "#fff", fontSize: 12, color: "#6b7280", lineHeight: 1, zIndex: 1, pointerEvents: "none" }}>Date</span>
            <Filters key={filtersKey} type="inventory-dashboard" dateOnly hidePartySearch hideFilter hideReset filterHandler={handleDateFilter} />
          </div>
          {(locationId || fromDate || toDate) && (
            <IconButton size="small" onClick={resetFilters} title="Reset filters" sx={{ color: "#7a8290" }}>
              <RestartAltIcon sx={{ fontSize: 18 }} />
            </IconButton>
          )}
          <IconButton onClick={loadAll} disabled={isLoading} sx={{ borderRadius: 2 }}>
            <RefreshIcon sx={{ fontSize: 20, color: TEXT }} />
          </IconButton>
        </Stack>
      </Stack>

      {/* KPI Row — only the numbers the user acts on */}
      <Grid container spacing={{ xs: 1, sm: 1.5 }} sx={{ mb: 1.5 }}>
        <Grid item xs={6} sm={4} md={2.4}>
          <MetricCard title="Total Items" value={fmtQty(summary.totalItems)} icon={<Inventory2OutlinedIcon />} accent={ACCENT} />
        </Grid>
        <Grid item xs={6} sm={4} md={2.4}>
          <MetricCard title="Stock In (30d)" value={fmtQty(summary.stockInCount30d)} sub="movements" icon={<ArrowDownwardOutlinedIcon />} accent="#22c55e" />
        </Grid>
        <Grid item xs={6} sm={4} md={2.4}>
          <MetricCard title="Stock Out (30d)" value={fmtQty(summary.stockOutCount30d)} sub="movements" icon={<ArrowUpwardOutlinedIcon />} accent="#ef4444" />
        </Grid>
        <Grid item xs={6} sm={4} md={2.4}>
          <MetricCard title="Low Stock" value={fmtQty(summary.lowStockCount)} icon={<WarningAmberOutlinedIcon />} accent="#f59e0b" />
        </Grid>
        <Grid item xs={6} sm={4} md={2.4}>
          <MetricCard title="Out of Stock" value={fmtQty(summary.outOfStockCount)} icon={<RemoveShoppingCartOutlinedIcon />} accent="#ef4444" />
        </Grid>
      </Grid>

      {/* Row: Stock by Location + Top Moving Products */}
      <Grid container spacing={{ xs: 1, sm: 1.5 }} sx={{ mb: 1.5 }}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ ...cardSx, p: { xs: 1.5, sm: 2 }, height: "100%" }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Typography sx={{ fontWeight: 700, color: TEXT, fontSize: "0.85rem" }}>Stock by Location</Typography>
              <Typography sx={{ color: ACCENT, fontSize: "0.78rem", fontWeight: 700 }}>{fmtQty(totalLocationQty)} units</Typography>
            </Stack>
            {loading ? (
              <Skeleton variant="rectangular" height={280} sx={{ borderRadius: 2 }} />
            ) : byLocation.length === 0 ? (
              <Box sx={{ height: 280, display: "flex", alignItems: "center", justifyContent: "center", color: MUTED, fontSize: "0.8rem" }}>No stock recorded at any location yet</Box>
            ) : (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, justifyContent: "center", minHeight: 280 }}>
                {byLocation.map((row: any) => (
                  <Box key={row.locationId}>
                    <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.4 }}>
                      <Typography sx={{ fontSize: "0.78rem", fontWeight: 500 }}>
                        {row.name || "Unknown"} <span style={{ color: MUTED, fontWeight: 400 }}>({row.type})</span>
                      </Typography>
                      <Typography sx={{ fontSize: "0.78rem", fontWeight: 600 }}>
                        {fmtQty(row.qty)} units <span style={{ color: MUTED, fontWeight: 400 }}>· {fmtQty(row.itemCount)} items</span>
                      </Typography>
                    </Stack>
                    <Box sx={{ height: 7, borderRadius: 4, backgroundColor: "#f0f1f3", overflow: "hidden" }}>
                      <Box sx={{ height: "100%", width: `${Math.max(2, (row.qty / maxLocationQty) * 100)}%`, backgroundColor: ACCENT, borderRadius: 4 }} />
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper sx={{ ...cardSx, p: { xs: 1.5, sm: 2 }, height: "100%" }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
              <Typography sx={{ fontWeight: 700, color: TEXT, fontSize: "0.85rem" }}>Top Moving Products</Typography>
              <Chip size="small" label="Last 30 days" sx={{ backgroundColor: ACCENT_SOFT, color: "#115e59", fontWeight: 600, fontSize: "0.62rem", height: 18, "& .MuiChip-label": { px: 0.75 } }} />
            </Stack>
            {loading ? (
              <Skeleton variant="rectangular" height={280} sx={{ borderRadius: 2 }} />
            ) : topProducts.length === 0 ? (
              <Box sx={{ height: 280, display: "flex", alignItems: "center", justifyContent: "center", color: MUTED, fontSize: "0.8rem" }}>No outbound stock movement in the last 30 days</Box>
            ) : (
              <Chart options={topProductsOptions as any} series={topProductsSeries} type="bar" height={280} />
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Row: Top / Low stock (tabbed) + Dead stock */}
      <Grid container spacing={{ xs: 1, sm: 1.5 }} sx={{ mb: 1.5 }}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ ...cardSx, p: { xs: 1.5, sm: 2 }, height: "100%" }}>
            <Tabs
              value={stockTab}
              onChange={(_e, v) => setStockTab(v)}
              sx={tabsSx}
            >
              <Tab label="Top Stock" />
              <Tab label={`Low / Out of Stock${lowStock.length ? ` (${lowStock.length})` : ""}`} />
            </Tabs>
            {stockTab === 0 ? (
              loading ? (
                <Skeleton variant="rectangular" height={280} sx={{ borderRadius: 2 }} />
              ) : topStock.length === 0 ? (
                <Box sx={{ height: 280, display: "flex", alignItems: "center", justifyContent: "center", color: MUTED, fontSize: "0.8rem" }}>No stock on hand yet</Box>
              ) : (
                <Box sx={{ maxHeight: 280, overflowY: "auto" }}>
                  {topStock.map((row: any, idx: number) => (
                    <Box
                      key={row.skuId}
                      onClick={() => openItemHistory(row.skuId)}
                      sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 1, borderBottom: idx < topStock.length - 1 ? "1px solid #f0f1f3" : "none", cursor: "pointer", "&:hover": { backgroundColor: "#fafafb" } }}
                    >
                      <Typography sx={{ fontSize: "0.7rem", fontWeight: 700, color: MUTED, width: 18, flexShrink: 0 }}>#{idx + 1}</Typography>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontSize: "0.8rem", fontWeight: 500, color: TEXT }} noWrap>
                          {row.name}
                        </Typography>
                        {row.itemCode && <Typography sx={{ fontSize: "0.68rem", color: MUTED }}>{row.itemCode}</Typography>}
                      </Box>
                      <Typography sx={{ fontSize: "0.82rem", fontWeight: 700, color: ACCENT, flexShrink: 0 }}>
                        {fmtQty(row.totalQty)} {row.baseUnitName}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )
            ) : loading ? (
              <Skeleton variant="rectangular" height={280} sx={{ borderRadius: 2 }} />
            ) : lowStock.length === 0 ? (
              <Box sx={{ height: 280, display: "flex", alignItems: "center", justifyContent: "center", color: MUTED, fontSize: "0.8rem", textAlign: "center", px: 2 }}>
                Nothing out of stock, and no item is below its reorder level.
              </Box>
            ) : (
              <Box sx={{ maxHeight: 280, overflowY: "auto" }}>
                {lowStock.map((row: any, idx: number) => (
                  <Box
                    key={row.skuId}
                    onClick={() => openItemHistory(row.skuId)}
                    sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 1, borderBottom: idx < lowStock.length - 1 ? "1px solid #f0f1f3" : "none", cursor: "pointer", "&:hover": { backgroundColor: "#fafafb" } }}
                  >
                    <Box sx={{ width: 8, height: 8, borderRadius: "50%", flexShrink: 0, backgroundColor: row.outOfStock ? "#ef4444" : "#f59e0b" }} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontSize: "0.8rem", fontWeight: 500, color: TEXT }} noWrap>
                        {row.name}
                      </Typography>
                      <Typography sx={{ fontSize: "0.68rem", color: MUTED }}>
                        {fmtQty(row.totalQty)} {row.baseUnitName} on-hand
                        {row.lowStockQuantity > 0 ? ` · reorder at ${fmtQty(row.lowStockQuantity)}` : ""}
                      </Typography>
                    </Box>
                    {row.outOfStock ? (
                      <Chip size="small" label="Out of stock" sx={{ backgroundColor: "#fee2e2", color: "#b91c1c", fontWeight: 700, fontSize: "0.62rem", height: 20, "& .MuiChip-label": { px: 1 } }} />
                    ) : (
                      <Typography sx={{ fontSize: "0.82rem", fontWeight: 700, color: "#ef4444", flexShrink: 0 }}>
                        short {fmtQty(row.deficit > 0 ? row.deficit : 0)}
                      </Typography>
                    )}
                  </Box>
                ))}
              </Box>
            )}
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper sx={{ ...cardSx, p: { xs: 1.5, sm: 2 }, height: "100%" }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.75 }}>
              <Stack direction="row" alignItems="center" spacing={0.25}>
                <Typography sx={{ fontWeight: 700, color: TEXT, fontSize: "0.85rem" }}>Dead Stock</Typography>
                <Tooltip title="Items with no stock movement in the last 60 days" arrow>
                  <IconButton
                    size="small"
                    aria-label="Dead stock information"
                    sx={{ p: 0.25, color: MUTED }}
                  >
                    <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              </Stack>
              <Chip size="small" label="No movement · 60 days" sx={{ backgroundColor: "#fee2e2", color: "#b91c1c", fontWeight: 600, fontSize: "0.62rem", height: 18, "& .MuiChip-label": { px: 0.75 } }} />
            </Stack>
            {loading ? (
              <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 2 }} />
            ) : deadStock.length === 0 ? (
              <Box sx={{ height: 300, display: "flex", alignItems: "center", justifyContent: "center", color: MUTED, fontSize: "0.8rem" }}>No dead stock — everything has moved recently</Box>
            ) : (
              <Box sx={{ maxHeight: 300, overflowY: "auto" }}>
                {deadStock.map((row: any, idx: number) => (
                  <Box
                    key={row.skuId}
                    onClick={() => openItemHistory(row.skuId)}
                    sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 1, borderBottom: idx < deadStock.length - 1 ? "1px solid #f0f1f3" : "none", cursor: "pointer", "&:hover": { backgroundColor: "#fafafb" } }}
                  >
                    <Box sx={{ width: 8, height: 8, borderRadius: "50%", flexShrink: 0, backgroundColor: "#ef4444" }} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontSize: "0.8rem", fontWeight: 500, color: TEXT }} noWrap>
                        {row.name}
                      </Typography>
                      <Typography sx={{ fontSize: "0.68rem", color: MUTED }}>{daysAgo(row.lastMovedAt)}</Typography>
                    </Box>
                    <Typography sx={{ fontSize: "0.82rem", fontWeight: 700, color: "#b91c1c", flexShrink: 0 }}>
                      {fmtQty(row.totalQty)} {row.baseUnitName}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Reports & details — kept last so the dashboard insights and charts come first. */}
      <Paper sx={{ ...cardSx, p: { xs: 1.5, sm: 2 }, mb: 1.5 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.25 }}>
          <AssessmentOutlinedIcon sx={{ fontSize: 18, color: "#059669" }} />
          <Typography sx={{ fontWeight: 700, color: TEXT, fontSize: "0.85rem" }}>Reports & Details</Typography>
          <Typography sx={{ color: MUTED, fontSize: "0.7rem" }}>· click a card to open the full page</Typography>
        </Stack>
        <Grid container spacing={1.5}>
          <Grid item xs={12} sm={6} md={3}>
            <ReportCard title="Stock In Hand Report" desc="Current stock of every item — location, batch and expiry wise" to="/inventory/stock/in-hand-report" download />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <ReportCard title="Stock Out Report" desc="Every outward movement — sale, wastage, transfer, adjustment" to="/inventory/stock/out-report" download />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <ReportCard title="Stock & Movements" desc="On-hand levels and the full in/out movement ledger" to="/inventory/stock" />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <ReportCard title="GRN (Goods In)" desc="Goods receipts — what came in from suppliers" to="/inventory/grn" download downloadTo="/inventory/grn/report" />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <ReportCard title="Requisitions" desc="Branch demands and their approval status" to="/inventory/requisitions" download downloadTo="/inventory/requisitions/report" />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <ReportCard title="Stock Transfers" desc="Stock moved between your locations" to="/inventory/transfers" download downloadTo="/inventory/transfers/report" />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <ReportCard title="Batches" desc="All batches with expiry, MRP and prices" to="/inventory/batches" />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <ReportCard title="Items" desc="Full item master — products, services, raw materials" to="/inventory" />
          </Grid>
        </Grid>
      </Paper>

      {/* Item movement-history drill-down — opened by clicking any item name. */}
      <Dialog open={!!historySkuId} onClose={closeItemHistory} fullWidth maxWidth="sm">
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pr: 1.5 }}>
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: "1rem" }}>{historyData?.sku?.name || "Item History"}</Typography>
            {historyData?.sku?.itemCode && (
              <Typography sx={{ fontSize: "0.7rem", color: MUTED }}>{historyData.sku.itemCode}</Typography>
            )}
          </Box>
          <IconButton size="small" onClick={closeItemHistory}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          {historyLoading ? (
            <Box sx={{ p: 2 }}>
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} height={48} sx={{ mb: 0.5 }} />
              ))}
            </Box>
          ) : !historyData || historyData.movements.length === 0 ? (
            <Box sx={{ py: 5, textAlign: "center", color: MUTED, fontSize: "0.85rem" }}>No stock movements recorded for this item.</Box>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", maxHeight: "60vh", overflowY: "auto" }}>
              {historyData.movements.map((m: any, idx: number) => (
                <Box
                  key={m._id}
                  sx={{ display: "flex", alignItems: "center", gap: 1.5, px: 2, py: 1.1, borderBottom: idx < historyData.movements.length - 1 ? "1px solid #f0f1f3" : "none" }}
                >
                  <Box sx={{ width: 8, height: 8, borderRadius: "50%", flexShrink: 0, backgroundColor: reasonAccent(m.reason) }} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: "0.78rem" }}>
                      <b>{movementReasonLabel(m.reason)}</b>
                      <span style={{ color: MUTED }}> at {m.locationId?.name || "—"}</span>
                      {m.batchId?.batchNumber && <span style={{ color: MUTED }}> · Batch {m.batchId.batchNumber}</span>}
                    </Typography>
                    <Typography sx={{ fontSize: "0.68rem", color: MUTED }}>{Utils.formatDate(m.createdAt)}</Typography>
                  </Box>
                  <Box sx={{ textAlign: "right", flexShrink: 0 }}>
                    <Typography sx={{ fontSize: "0.8rem", fontWeight: 700, color: m.qtyDelta < 0 ? "#ef4444" : "#22c55e" }}>
                      {m.qtyDelta > 0 ? `+${m.qtyDelta}` : m.qtyDelta}
                    </Typography>
                    <Typography sx={{ fontSize: "0.65rem", color: MUTED }}>bal. {m.balanceAfter}</Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default InventoryDashboard;
